import { useState, useMemo, useCallback, useEffect } from "react";
import {
  CryptoAlertData,
  LeaderboardAlertsResponse,
} from "@crypto-trading/common/browser";
import {
  getCachedPricesForAlerts,
  setCachedPrice,
} from "./leaderboardPriceCache";

interface PriceEntry {
  price: number | null;
  change: number | null;
}

export interface UseLeaderboardReturn {
  /** All listings + delistings combined (for WS price subscriptions) */
  allAlerts: CryptoAlertData[];
  /** Listings sorted best → worst change (unpriced at bottom) */
  sortedGainers: CryptoAlertData[];
  /** Delistings sorted worst → best change (unpriced at bottom) */
  sortedLosers: CryptoAlertData[];
  /** Subset of sortedGainers that have a live price change */
  pricedGainers: CryptoAlertData[];
  /** Subset of sortedLosers that have a live price change */
  pricedLosers: CryptoAlertData[];
  /**
   * True while the first WS price hasn't arrived yet.
   * Tiles must still be rendered (but hidden) so they can subscribe.
   */
  isPriceLoading: boolean;
  /** Callback to pass to each LeaderboardTokenTile */
  handlePriceUpdate: (
    id: string,
    price: number | null,
    change: number | null
  ) => void;
}

/**
 * Shared leaderboard logic used by both the desktop and mobile Analyze pages.
 *
 * All sorting and filtering is done here so the pages only handle UI concerns
 * (how many items to show, show-more state, etc.).
 *
 * Important: tiles must be rendered in the DOM even while `isPriceLoading` is true.
 * Hide them with CSS (`hidden` class) — do NOT conditionally unmount them — otherwise
 * the WS subscriptions never start and `isPriceLoading` stays true forever.
 */
export const useLeaderboard = (
  leaderboardData: LeaderboardAlertsResponse | undefined,
  isLoadingLeaderboard: boolean
): UseLeaderboardReturn => {
  const listings = useMemo(
    () => leaderboardData?.listings ?? [],
    [leaderboardData]
  );

  const delistings = useMemo(
    () => leaderboardData?.delistings ?? [],
    [leaderboardData]
  );

  const allAlerts = useMemo(
    () => [...listings, ...delistings],
    [listings, delistings]
  );

  const alertIdsKey = useMemo(
    () =>
      allAlerts
        .map((a) => a.id)
        .sort((a, b) => a.localeCompare(b))
        .join(","),
    [leaderboardData]
  );

  const [priceUpdates, setPriceUpdates] = useState<Map<string, PriceEntry>>(
    () => {
      if (allAlerts.length === 0) return new Map();
      const cached = getCachedPricesForAlerts(allAlerts.map((a) => a.id));
      return cached.size > 0 ? cached : new Map();
    }
  );

  useEffect(() => {
    if (allAlerts.length === 0) return;
    const cached = getCachedPricesForAlerts(allAlerts.map((a) => a.id));
    if (cached.size > 0) {
      setPriceUpdates((prev) => {
        const next = new Map(prev);
        for (const [id, entry] of cached) {
          next.set(id, entry);
        }
        return next;
      });
    }
  }, [alertIdsKey]);

  const handlePriceUpdate = useCallback(
    (id: string, price: number | null, change: number | null) => {
      setPriceUpdates((prev) => {
        const existing = prev.get(id);
        // Don't overwrite with null when tiles remount - preserves cache on back navigation
        if (price === null && change === null && existing) {
          return prev;
        }
        const entry: PriceEntry = { price, change };
        setCachedPrice(id, entry);
        const next = new Map(prev);
        next.set(id, entry);
        return next;
      });
    },
    []
  );

  // True only while API data is loaded but no WS price has arrived yet.
  // The consumer must NOT gate tile rendering on this flag.
  const isPriceLoading =
    !isLoadingLeaderboard &&
    allAlerts.length > 0 &&
    priceUpdates.size <= allAlerts.length / 2;

  // Unpriced alerts sink to the bottom via ±Infinity sentinel so they are
  // still rendered (hidden) and can subscribe for prices.
  const sortedGainers = useMemo(
    () =>
      [...listings].sort((a, b) => {
        const ca = priceUpdates.get(a.id)?.change ?? -Infinity;
        const cb = priceUpdates.get(b.id)?.change ?? -Infinity;
        return cb - ca;
      }),
    [listings, priceUpdates]
  );

  const sortedLosers = useMemo(
    () =>
      [...delistings].sort((a, b) => {
        const ca = priceUpdates.get(a.id)?.change ?? Infinity;
        const cb = priceUpdates.get(b.id)?.change ?? Infinity;
        return ca - cb;
      }),
    [delistings, priceUpdates]
  );

  const pricedGainers = useMemo(
    () => sortedGainers.filter((a) => priceUpdates.get(a.id)?.change != null),
    [sortedGainers, priceUpdates]
  );

  const pricedLosers = useMemo(
    () => sortedLosers.filter((a) => priceUpdates.get(a.id)?.change != null),
    [sortedLosers, priceUpdates]
  );

  return {
    allAlerts,
    sortedGainers,
    sortedLosers,
    pricedGainers,
    pricedLosers,
    isPriceLoading,
    handlePriceUpdate,
  };
};
